import React, { useState } from 'react';
import { Eye, ShieldCheck, LogOut } from 'lucide-react';

interface Props {
  hostName: string;
  /** Un examen en vez de una sala: cambia el texto, no lo que se graba. */
  exam?: boolean;
  onAccept: () => void;
  onLeave: () => void;
}

/**
 * Consentimiento previo para jugar en una sala supervisada.
 *
 * Es una pantalla aparte, no un aviso: mientras no se acepte no se graba nada
 * (el servidor tampoco guarda eventos de quien no ha aceptado) y se puede salir
 * sin consecuencias. El consentimiento tiene que ser informado, libre y
 * anterior a la grabación; por eso dice qué se graba, qué no, quién lo ve,
 * cuánto se conserva y cómo pedir que se borre.
 */
export const MonitorConsent: React.FC<Props> = ({ hostName, exam = false, onAccept, onLeave }) => {
  const [confirmed, setConfirmed] = useState(false);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-8">
      <div className="surface-raised max-w-xl w-full p-6 sm:p-8 space-y-5 animate-pop">
        <div className="flex items-center gap-3">
          <span className="p-2.5 rounded-xl bg-sky-500/15 text-sky-400">
            <Eye className="w-6 h-6" />
          </span>
          <h1 className="text-xl sm:text-2xl font-display font-bold">{exam ? 'Examen supervisado' : 'Sala supervisada'}</h1>
        </div>

        <p className="text-ink-200">
          <strong>{hostName}</strong> ha activado la supervisión anti-trampas en{' '}
          {exam ? 'este examen' : 'esta sala'}. Para {exam ? 'hacerlo' : 'jugar'} necesitamos tu
          permiso para grabar tu pantalla durante {exam ? 'el examen' : 'la partida'}.
        </p>

        <ul className="space-y-2.5 text-sm text-ink-300 list-disc pl-5">
          <li>
            <strong className="text-ink-100">Se graba</strong> lo que haces dentro de esta pestaña
            de Open Dahu: clics, movimientos y cambios en la pantalla. Los campos de texto salen
            tapados. También se cuentan las veces que sales de la pestaña, pierdes el foco, copias
            o pegas.
          </li>
          <li>
            <strong className="text-ink-100">No se graba</strong> otra pestaña, otra aplicación, tu
            cámara, tu micrófono ni nada fuera de Open Dahu.
          </li>
          <li>
            <strong className="text-ink-100">Quién la ve:</strong>{' '}
            {exam
              ? 'solo quien creó el examen, en sus grabaciones.'
              : 'solo el anfitrión de la sala, en directo y después en sus grabaciones.'}
          </li>
          <li>
            <strong className="text-ink-100">Cuánto se guarda:</strong> se borra automáticamente a
            los 30 días. Si tienes cuenta, también se borra al eliminarla. Puedes pedir que se borre
            antes escribiendo al contacto de la{' '}
            <a href="/privacidad" target="_blank" rel="noopener" className="text-sky-400 underline">
              Política de privacidad
            </a>
            .
          </li>
          <li>
            <strong className="text-ink-100">Es tu decisión:</strong> si no aceptas,{' '}
            {exam ? 'no se empieza el examen' : 'sales de la sala'} y no pasa nada. Si aceptas, puedes salir cuando quieras; lo ya grabado se
            conserva hasta que se borre.
          </li>
          <li>
            Las cuentas de menores de 18 años no se graban.
          </li>
        </ul>

        <label className="flex items-start gap-3 rounded-xl border border-ink-700 bg-ink-850 p-3.5 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 w-4 h-4 accent-sky-500"
          />
          <span className="text-sm text-ink-200">
            He leído esto y acepto que se grabe mi pantalla de Open Dahu en{' '}
            {exam ? 'este examen' : 'esta sala'}. Confirmo que
            tengo edad para decidirlo, o que mi madre, padre o tutor me ha dado permiso.
          </span>
        </label>

        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <button
            onClick={onLeave}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            No acepto, salir
          </button>
          <button
            onClick={onAccept}
            disabled={!confirmed}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
            Acepto y entro
          </button>
        </div>
      </div>
    </div>
  );
};
